import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  BookOpen,
  Loader2,
  LogOut,
  Mail,
  Save,
  Upload,
  User,
} from "lucide-react";
import {
  extractTeacherRecord,
  getTeacherDisplayName,
  getTeacherEmail,
  getTeacherPhotoValue,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  clearTeacherSession,
  updateTeacherProfile,
} from "../../features/teacher/teacherSlice";
import {
  normalizeAudPriceForDisplay,
  normalizeAudPriceToMinorUnits,
} from "../../utils/audCurrency";
import { resolveApiImageUrl } from "../../utils/imageUrl";
import {
  filterSupportedTeacherYearIds,
  formatTeacherYearLabels,
  TEACHER_YEAR_OPTIONS,
} from "../../utils/teacherYears";
import {
  getTeacherPhoneValidationMessage,
  normalizeTeacherPhoneNumber,
} from "../../utils/teacherPhone";

const SPECIALIZATION_OPTIONS = [
  "Visual learning",
  "Exam preparation",
  "Problem solving",
  "Mental maths",
  "Foundational maths",
  "Advanced concepts",
];
const MAX_IMAGE_REFERENCE_LENGTH = 500;

const updateProfileStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap');

  .teacher-update-page {
    min-height: 100vh;
    background:
      radial-gradient(circle at top left, rgba(255, 194, 142, 0.28), transparent 26%),
      linear-gradient(180deg, #fcf7ef 0%, #f7f1e7 100%);
    color: #1f1a17;
    font-family: "Lato", sans-serif;
  }

  .teacher-update-topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.95);
    border-bottom: 1px solid #ebe4d8;
    backdrop-filter: blur(14px);
  }

  .teacher-update-shell {
    width: min(100%, 78rem);
    margin: 0 auto;
    padding-inline: clamp(0.875rem, 2vw, 1.25rem);
  }

  .teacher-update-topbar-row {
    min-height: clamp(4rem, 5.5vw, 4.5rem);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .teacher-update-brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .teacher-update-back {
    width: 2.45rem;
    height: 2.45rem;
    border: 1px solid #eadfd6;
    border-radius: 999px;
    background: #ffffff;
    color: #ff624d;
    display: grid;
    place-items: center;
    cursor: pointer;
    box-shadow: 0 10px 20px rgba(111, 92, 52, 0.08);
    transition: border-color 180ms ease, box-shadow 180ms ease,
      transform 180ms ease, color 180ms ease;
  }

  .teacher-update-back:hover,
  .teacher-update-back:focus-visible {
    outline: none;
    border-color: rgba(255, 98, 77, 0.42);
    box-shadow: 0 14px 24px rgba(255, 98, 77, 0.14);
    color: #ef4b43;
    transform: translateY(-1px);
  }

  .teacher-update-brand-badge {
    width: clamp(2.25rem, 2.8vw, 2.45rem);
    height: clamp(2.25rem, 2.8vw, 2.45rem);
    border: 0;
    padding: 0;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    color: #ffffff;
    cursor: pointer;
    box-shadow: 0 12px 20px rgba(255, 98, 77, 0.2);
    transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease;
  }

  .teacher-update-brand-badge:hover,
  .teacher-update-brand-badge:focus-visible {
    outline: none;
    transform: translateY(-1px);
    box-shadow: 0 16px 24px rgba(255, 98, 77, 0.24);
    filter: saturate(1.05);
  }

  .teacher-update-brand-title {
    margin: 0;
    color: #ff624d;
    font-size: clamp(1.1rem, 1.7vw, 1.35rem);
    font-weight: 700;
  }

  .teacher-update-actions {
    display: flex;
    align-items: center;
    gap: 0.9rem;
  }

  .teacher-update-email-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    min-height: 2.5rem;
    padding-inline: 1rem;
    border: 1px solid #dbe3f2;
    border-radius: 999px;
    background: #ffffff;
    color: #6f6a67;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  .teacher-update-logout {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    border: 0;
    background: transparent;
    color: #1f1a17;
    font: inherit;
    font-size: 0.94rem;
    cursor: pointer;
  }

  .teacher-update-content {
    padding: clamp(1.25rem, 2.5vw, 1.75rem) clamp(0.875rem, 1.6vw, 1.25rem)
      clamp(2rem, 4vw, 2.75rem);
  }

  .teacher-update-banner {
    border: 1px solid #dbe5fb;
    border-radius: 1.25rem;
    background: #dfe8fb;
    padding: 1.25rem 1.35rem;
    margin-bottom: 1.25rem;
  }

  .teacher-update-banner h1 {
    margin: 0 0 0.45rem;
    font-size: 1.2rem;
    font-weight: 800;
  }

  .teacher-update-banner p {
    margin: 0;
    color: #6a6c77;
  }

  .teacher-update-status {
    border-radius: 1rem;
    border: 1px solid transparent;
    padding: 0.95rem 1rem;
    margin-bottom: 1rem;
    font-size: 0.94rem;
  }

  .teacher-update-status-error {
    border-color: #ffd1c7;
    background: #fff3f0;
    color: #b34736;
  }

  .teacher-update-status-success {
    border-color: #d7efd7;
    background: #effbf0;
    color: #2f7c35;
  }

  .teacher-update-card {
    border: 1px solid #e6e0d6;
    border-radius: 1.25rem;
    background: rgba(255, 255, 255, 0.95);
    padding: clamp(1rem, 2vw, 1.6rem);
    box-shadow: 0 24px 40px rgba(111, 92, 52, 0.08);
  }

  .teacher-update-card-title {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    margin-bottom: 1.25rem;
    font-size: 1.1rem;
    font-weight: 800;
  }

  .teacher-update-photo-row {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    margin-bottom: 1.5rem;
  }

  .teacher-update-photo-box {
    width: 6rem;
    height: 6rem;
    border-radius: 1rem;
    overflow: hidden;
    background: #101010;
    display: grid;
    place-items: center;
    color: #ffffff;
    font-size: 1.35rem;
    font-weight: 800;
    flex: 0 0 auto;
    box-shadow: 0 16px 26px rgba(0, 0, 0, 0.16);
  }

  .teacher-update-photo-box img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  .teacher-update-photo-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.7rem;
  }

  .teacher-update-upload-note {
    color: #7a7673;
    font-size: 0.9rem;
  }

  .teacher-update-upload-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
  }

  .teacher-update-upload-button {
    min-height: 2.8rem;
    padding: 0.7rem 1rem;
    border: 1px solid #ff624d;
    border-radius: 0.8rem;
    background: #ff624d;
    color: #ffffff;
    font: inherit;
    font-size: 0.94rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    cursor: pointer;
  }

  .teacher-update-upload-button:hover {
    background: #ff624d;
  }

  .teacher-update-upload-file-name {
    color: #6f6a67;
    font-size: 0.88rem;
    line-height: 1.5;
    word-break: break-word;
  }

  .teacher-update-file-input {
    display: none;
  }

  .teacher-update-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .teacher-update-field {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }

  .teacher-update-field-note {
    margin: -0.1rem 0 0;
    color: #7a7673;
    font-size: 0.88rem;
    line-height: 1.5;
  }

  .teacher-update-field-error {
    margin: -0.1rem 0 0;
    color: #b34736;
    font-size: 0.88rem;
    line-height: 1.5;
  }

  .teacher-update-field-full {
    grid-column: 1 / -1;
  }

  .teacher-update-field label {
    font-size: 0.96rem;
    font-weight: 700;
  }

  .teacher-update-input,
  .teacher-update-textarea {
    width: 100%;
    min-height: 3.1rem;
    border: 1px solid #e2e7ee;
    border-radius: 0.85rem;
    background: #ffffff;
    color: #1f1a17;
    font: inherit;
    font-size: 0.95rem;
    padding: 0.85rem 1rem;
  }

  .teacher-update-input-error {
    border-color: #dc2626;
    background: #fff8f8;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
  }

  .teacher-update-input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  .teacher-update-input[type="number"]::-webkit-outer-spin-button,
  .teacher-update-input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .teacher-update-textarea {
    min-height: 7rem;
    resize: vertical;
  }

  .teacher-update-chip-section {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .teacher-update-chip-section-error {
    border: 1px solid #dc2626;
    border-radius: 0.85rem;
    background: #fff8f8;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.08);
    padding: 0.85rem;
  }

  .teacher-update-chip-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .teacher-update-chip-grid-specializations {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .teacher-update-chip {
    min-height: 3rem;
    border: 1px solid #e1e5ec;
    border-radius: 0.8rem;
    background: #ffffff;
    color: #2a2522;
    font: inherit;
    font-size: 0.94rem;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
  }

  .teacher-update-chip-active {
    border-color: #ff6f59;
    background: #fff1ec;
    color: #c94d3d;
    font-weight: 700;
  }

  .teacher-update-footer {
    margin-top: 1rem;
  }

  .teacher-update-save {
    width: 100%;
    min-height: 3.35rem;
    border: 0;
    border-radius: 0.9rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    box-shadow: 0 18px 28px rgba(255, 98, 77, 0.22);
    color: #ffffff;
    font: inherit;
    font-size: 1rem;
    font-weight: 800;
    cursor: pointer;
  }

  .teacher-update-save:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }

  @media (max-width: 900px) {
    .teacher-update-chip-grid,
    .teacher-update-chip-grid-specializations {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 768px) {
    .teacher-update-topbar-row {
      min-height: auto;
      padding: 1rem 0;
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-update-actions {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .teacher-update-email-pill,
    .teacher-update-logout {
      justify-content: center;
    }

    .teacher-update-photo-row {
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-update-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 540px) {
    .teacher-update-chip-grid,
    .teacher-update-chip-grid-specializations {
      grid-template-columns: 1fr;
    }
  }
`;

const toTrimmedString = (value) =>
  value === null || typeof value === "undefined" ? "" : String(value).trim();

const JPEG_IMAGE_REFERENCE_PATTERN = /\.jpe?g(?:[?#].*)?$/i;
const JPEG_DATA_URL_PATTERN = /^data:image\/jpeg(?:;base64)?,/i;
const PHOTO_IMAGE_ERROR_MESSAGE =
  "Photo image must be in .jpg or .jpeg format only.";
const FULL_NAME_PATTERN = /^[A-Za-z][A-Za-z\s'.-]{1,99}$/;
const PRICE_PER_SESSION_INCREMENT = 5;

const pickFirstValue = (...values) =>
  values.map((value) => toTrimmedString(value)).find(Boolean) || "";

const parseList = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => parseList(item));
  }

  if (value && typeof value === "object") {
    return Object.values(value).flatMap((item) => parseList(item));
  }

  const normalized = toTrimmedString(value);
  if (!normalized) return [];

  return normalized
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const getPhotoUrl = (teacher = {}) => resolveApiImageUrl(getTeacherPhotoValue(teacher));

const getInitials = (value = "") =>
  String(value)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "TP";

const getPhotoImageValidationMessage = (value) => {
  const normalizedValue = toTrimmedString(value);
  if (!normalizedValue) return "";

  if (
    !JPEG_DATA_URL_PATTERN.test(normalizedValue) &&
    normalizedValue.length > MAX_IMAGE_REFERENCE_LENGTH
  ) {
    return `Max ${MAX_IMAGE_REFERENCE_LENGTH} characters allowed`;
  }

  if (
    JPEG_IMAGE_REFERENCE_PATTERN.test(normalizedValue) ||
    JPEG_DATA_URL_PATTERN.test(normalizedValue)
  ) {
    return "";
  }

  return PHOTO_IMAGE_ERROR_MESSAGE;
};

const getNameValidationMessage = (value) => {
  const normalizedValue = toTrimmedString(value);
  if (!normalizedValue) {
    return "Full Name is required.";
  }

  if (!FULL_NAME_PATTERN.test(normalizedValue)) {
    return "Please enter a valid full name.";
  }

  return "";
};

const getPhoneValidationMessage = (value) => {
  return getTeacherPhoneValidationMessage(value);
};

const getCertificateImageValidationMessage = (value) => {
  const normalizedValue = toTrimmedString(value);
  if (!normalizedValue) return "";

  if (
    !JPEG_DATA_URL_PATTERN.test(normalizedValue) &&
    normalizedValue.length > MAX_IMAGE_REFERENCE_LENGTH
  ) {
    return `Max ${MAX_IMAGE_REFERENCE_LENGTH} characters allowed`;
  }

  return "";
};

const getQualificationsValidationMessage = (value) => {
  if (!toTrimmedString(value)) {
    return "Qualification is required.";
  }

  return "";
};

const getExperienceValidationMessage = (value) => {
  const normalizedValue = toTrimmedString(value);
  if (!normalizedValue) {
    return "Years of Experience is required.";
  }

  const numericValue = Number(normalizedValue);
  if (
    !Number.isFinite(numericValue) ||
    numericValue < 0 ||
    !Number.isInteger(numericValue)
  ) {
    return "Years of Experience must be 0 or more.";
  }

  return "";
};

const getSubjectsValidationMessage = (value) => {
  if (!toTrimmedString(value)) {
    return "Subjects is required.";
  }

  return "";
};

const getPriceValidationMessage = (value) => {
  const normalizedValue = toTrimmedString(value);
  if (!normalizedValue) {
    return "Price Per Session is required.";
  }

  const numericValue = Number(normalizedValue);
  if (
    !Number.isFinite(numericValue) ||
    numericValue < PRICE_PER_SESSION_INCREMENT ||
    !Number.isInteger(numericValue) ||
    numericValue % PRICE_PER_SESSION_INCREMENT !== 0
  ) {
    return `Price must be ${PRICE_PER_SESSION_INCREMENT} or more in multiples of ${PRICE_PER_SESSION_INCREMENT}.`;
  }

  return "";
};

const getYearsYouTeachValidationMessage = (value) =>
  Array.isArray(value) && value.length ? "" : "Select at least one year.";

const getSpecializationsValidationMessage = (value) =>
  Array.isArray(value) && value.length
    ? ""
    : "Select at least one specialization.";

const getFieldValidationMessage = (field, value) => {
  switch (field) {
    case "name":
      return getNameValidationMessage(value);
    case "phone":
      return getPhoneValidationMessage(value);
    case "photoImage":
      return getPhotoImageValidationMessage(value);
    case "certificateImage":
      return getCertificateImageValidationMessage(value);
    case "qualifications":
      return getQualificationsValidationMessage(value);
    case "experience":
      return getExperienceValidationMessage(value);
    case "subjects":
      return getSubjectsValidationMessage(value);
    case "pricePerSession":
      return getPriceValidationMessage(value);
    case "yearsYouTeach":
      return getYearsYouTeachValidationMessage(value);
    case "specializations":
      return getSpecializationsValidationMessage(value);
    default:
      return "";
  }
};

const getFormValidationErrors = ({ formState, localUploads }) => {
  const photoImageCandidate = localUploads.photoImage || formState.photoImage;
  const certificateImageCandidate =
    localUploads.certificateImage || formState.certificateImage;

  return {
    name: getNameValidationMessage(formState.name),
    phone: getPhoneValidationMessage(formState.phone),
    photoImage: getPhotoImageValidationMessage(photoImageCandidate),
    certificateImage: getCertificateImageValidationMessage(
      certificateImageCandidate
    ),
    qualifications: getQualificationsValidationMessage(
      formState.qualifications
    ),
    experience: getExperienceValidationMessage(formState.experience),
    subjects: getSubjectsValidationMessage(formState.subjects),
    pricePerSession: getPriceValidationMessage(formState.pricePerSession),
    yearsYouTeach: getYearsYouTeachValidationMessage(formState.yearsYouTeach),
    specializations: getSpecializationsValidationMessage(
      formState.specializations
    ),
  };
};

const isAcceptedPhotoUpload = (file) => {
  const normalizedFileName = toTrimmedString(file?.name);
  const normalizedFileType = toTrimmedString(file?.type).toLowerCase();

  return (
    JPEG_IMAGE_REFERENCE_PATTERN.test(normalizedFileName) ||
    ["image/jpeg", "image/jpg", "image/pjpeg"].includes(normalizedFileType)
  );
};

const buildUpdatePayload = (formState, initialFormState) => {
  const payload = {};
  const normalizedCurrentPhone =
    normalizeTeacherPhoneNumber(formState.phone) || formState.phone.trim();
  const normalizedInitialPhone =
    normalizeTeacherPhoneNumber(initialFormState.phone) || initialFormState.phone.trim();

  if (formState.name !== initialFormState.name) {
    payload.name = formState.name.trim();
  }

  if (normalizedCurrentPhone !== normalizedInitialPhone) {
    payload.phone = normalizedCurrentPhone;
  }

  if (formState.bio !== initialFormState.bio) {
    payload.bio = formState.bio.trim();
  }

  if (formState.photoImage !== initialFormState.photoImage) {
    payload.photoImage = formState.photoImage.trim();
  }

  if (formState.certificateImage !== initialFormState.certificateImage) {
    payload.certificateImage = formState.certificateImage.trim();
  }

  if (formState.qualifications !== initialFormState.qualifications) {
    payload.qualifications = formState.qualifications.trim();
  }

  if (formState.specializations.join(", ") !== initialFormState.specializations.join(", ")) {
    payload.specializations = formState.specializations.join(", ");
  }

  if (formState.subjects !== initialFormState.subjects) {
    payload.subjects = formState.subjects.trim();
  }

  if (formState.pricePerSession !== initialFormState.pricePerSession) {
    payload.price_per_session =
      normalizeAudPriceToMinorUnits(formState.pricePerSession) || 0;
  }

  if (formState.experience !== initialFormState.experience) {
    payload.experience = Number(formState.experience || 0);
  }

  if (JSON.stringify(formState.yearsYouTeach) !== JSON.stringify(initialFormState.yearsYouTeach)) {
    payload.year = formState.yearsYouTeach;
  }

  return payload;
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Unable to read the selected file."));
    reader.readAsDataURL(file);
  });

const createFormState = (teacher = {}) => ({
  name: pickFirstValue(teacher.name, teacher.full_name),
  phone: normalizeTeacherPhoneNumber(pickFirstValue(teacher.phone)) || pickFirstValue(teacher.phone),
  bio: pickFirstValue(teacher.bio),
  photoImage: getTeacherPhotoValue(teacher),
  certificateImage: pickFirstValue(teacher.certificateImage),
  qualifications: pickFirstValue(teacher.qualifications, teacher.qualification),
  experience: pickFirstValue(teacher.experience),
  subjects: pickFirstValue(teacher.subjects),
  pricePerSession:
    normalizeAudPriceForDisplay(pickFirstValue(teacher.price_per_session)) ?? "",
  yearsYouTeach: filterSupportedTeacherYearIds(
    teacher?.year_labels?.length ? teacher.year_labels : teacher.year
  ),
  specializations: parseList(
    teacher.specializations ||
      teacher.teaching_specializations ||
      teacher.specialization
  ),
});

const createInitialFieldErrors = (teacher = {}) => ({
  name: "",
  phone: "",
  bio: "",
  photoImage: getPhotoImageValidationMessage(getTeacherPhotoValue(teacher)),
  certificateImage: "",
  qualifications: "",
  experience: "",
  subjects: "",
  pricePerSession: "",
  yearsYouTeach: "",
  specializations: "",
});

const FIELD_ERROR_ALIASES = {
  name: ["name", "full_name", "fullName"],
  phone: ["phone", "phone_number", "mobile", "mobile_number"],
  bio: ["bio", "about"],
  photoImage: ["photoImage", "photo_image", "photo", "profile_photo", "profilePhoto", "image"],
  certificateImage: ["certificateImage", "certificate_image", "certificate"],
  qualifications: ["qualifications", "qualification"],
  experience: ["experience"],
  subjects: ["subjects", "subject"],
  pricePerSession: ["price_per_session", "pricePerSession", "price"],
  yearsYouTeach: ["year", "years", "year_id", "yearsYouTeach"],
  specializations: ["specializations", "specialization", "teaching_specializations"],
};

const FIELD_FOCUS_ORDER = [
  "name",
  "phone",
  "bio",
  "photoImage",
  "certificateImage",
  "qualifications",
  "experience",
  "subjects",
  "pricePerSession",
  "yearsYouTeach",
  "specializations",
];

const FIELD_STATUS_LABELS = {
  name: "Name",
  phone: "Phone",
  bio: "Bio",
  photoImage: "Profile photo",
  certificateImage: "Certificate image",
  qualifications: "Qualifications",
  experience: "Experience",
  subjects: "Subjects",
  pricePerSession: "Price per session",
  yearsYouTeach: "Years you teach",
  specializations: "Specializations",
};

const normalizeFieldErrors = (fieldErrors = {}, teacher = {}) => {
  const nextFieldErrors = createInitialFieldErrors(teacher);

  Object.entries(FIELD_ERROR_ALIASES).forEach(([fieldKey, aliases]) => {
    const matchedAlias = aliases.find((alias) => {
      const value = fieldErrors?.[alias];
      return String(value || "").trim();
    });

    if (matchedAlias) {
      nextFieldErrors[fieldKey] = String(fieldErrors[matchedAlias] || "").trim();
    }
  });

  return nextFieldErrors;
};

const focusFirstFieldError = (fieldErrors = {}, fieldRefs = {}) => {
  const firstInvalidField = FIELD_FOCUS_ORDER.find((fieldKey) =>
    Boolean(String(fieldErrors?.[fieldKey] || "").trim())
  );

  if (!firstInvalidField) {
    return;
  }

  const target = fieldRefs[firstInvalidField];
  if (!target) {
    return;
  }

  target.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });

  if (typeof target.focus === "function") {
    target.focus({ preventScroll: true });
  }
};

const getFieldErrorStatusMessage = (fieldErrors = {}) => {
  const firstInvalidField = FIELD_FOCUS_ORDER.find((fieldKey) =>
    Boolean(String(fieldErrors?.[fieldKey] || "").trim())
  );

  if (!firstInvalidField) {
    return "";
  }

  const fieldLabel = FIELD_STATUS_LABELS[firstInvalidField] || "This field";
  const fieldMessage = String(fieldErrors?.[firstInvalidField] || "").trim();

  return fieldMessage
    ? `${fieldLabel}: ${fieldMessage}`
    : `There is an error in the ${fieldLabel.toLowerCase()} field.`;
};

export default function TeacherUpdateProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const teacherSession = useSelector((state) => state.teacher?.teacher);
  const initialFormRef = useRef(createFormState(teacherSession || {}));
  const photoFileInputRef = useRef(null);
  const certificateFileInputRef = useRef(null);
  const fieldRefs = useRef({});
  const [formState, setFormState] = useState(() => createFormState(teacherSession || {}));
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(() => getPhotoUrl(teacherSession || {}));
  const [photoPreviewFailed, setPhotoPreviewFailed] = useState(false);
  const [localUploads, setLocalUploads] = useState({
    photoImage: "",
    certificateImage: "",
  });
  const [localUploadNames, setLocalUploadNames] = useState({
    photoImage: "",
    certificateImage: "",
  });
  const [fieldErrors, setFieldErrors] = useState(() =>
    createInitialFieldErrors(teacherSession || {})
  );
  const [statusMessage, setStatusMessage] = useState("");

  const profileQuery = useQuery({
    queryKey: ["teacher-profile-update"],
    queryFn: teacherApi.getProfile,
    retry: false,
  });

  useEffect(() => {
    const teacherRecord = extractTeacherRecord(profileQuery.data, teacherSession || {});

    if (!teacherRecord) return;

    dispatch(updateTeacherProfile(teacherRecord));
    const nextFormState = createFormState(teacherRecord);
    initialFormRef.current = nextFormState;
    setFormState(nextFormState);
    setPhotoPreviewUrl(getPhotoUrl(teacherRecord));
    setPhotoPreviewFailed(false);
    setLocalUploads({
      photoImage: "",
      certificateImage: "",
    });
    setLocalUploadNames({
      photoImage: "",
      certificateImage: "",
    });
    setFieldErrors(createInitialFieldErrors(teacherRecord));
  }, [dispatch, profileQuery.data]);

  const teacherProfile = useMemo(
    () => extractTeacherRecord(profileQuery.data, teacherSession || {}) || teacherSession || {},
    [profileQuery.data, teacherSession]
  );

  const teacherEmail = getTeacherEmail(teacherProfile) || "Teacher account";
  const teacherName = getTeacherDisplayName(teacherProfile);

  const updateProfileMutation = useMutation({
    mutationFn: (payload) => teacherApi.updateProfile(payload),
    onSuccess: (response) => {
      const resolvedPhotoImage = localUploads.photoImage || formState.photoImage;
      const resolvedCertificateImage =
        localUploads.certificateImage || formState.certificateImage;
      const resolvedYearLabels = formatTeacherYearLabels(formState.yearsYouTeach);
      const teacherRecord = extractTeacherRecord(response, {
        ...teacherProfile,
        name: formState.name,
        phone:
          normalizeTeacherPhoneNumber(formState.phone) || formState.phone.trim(),
        bio: formState.bio.trim(),
        photoImage: resolvedPhotoImage,
        certificateImage: resolvedCertificateImage,
        qualifications: formState.qualifications,
        experience: formState.experience,
        subjects: formState.subjects,
        price_per_session:
          normalizeAudPriceToMinorUnits(formState.pricePerSession) || 0,
        year: formState.yearsYouTeach,
        year_labels: resolvedYearLabels,
        specializations: formState.specializations,
      }) || {
        ...teacherProfile,
        name: formState.name,
        phone:
          normalizeTeacherPhoneNumber(formState.phone) || formState.phone.trim(),
        bio: formState.bio.trim(),
        photoImage: resolvedPhotoImage,
        certificateImage: resolvedCertificateImage,
        qualifications: formState.qualifications,
        experience: formState.experience,
        subjects: formState.subjects,
        price_per_session:
          normalizeAudPriceToMinorUnits(formState.pricePerSession) || 0,
        year: formState.yearsYouTeach,
        year_labels: resolvedYearLabels,
        specializations: formState.specializations,
      };

      dispatch(updateTeacherProfile(teacherRecord));
      setLocalUploads({
        photoImage: "",
        certificateImage: "",
      });
      setLocalUploadNames({
        photoImage: "",
        certificateImage: "",
      });
      setFieldErrors(createInitialFieldErrors(teacherRecord));
      navigate("/teacherprofile", {
        replace: true,
        state: {
          message: "Profile updated successfully.",
          messageType: "success",
        },
      });
    },
    onError: (error) => {
      const nextFieldErrors = normalizeFieldErrors(
        error?.fieldErrors,
        teacherProfile
      );
      const hasFieldErrors = Object.values(nextFieldErrors).some(Boolean);

      setFieldErrors(nextFieldErrors);
      setStatusMessage(
        hasFieldErrors
          ? getFieldErrorStatusMessage(nextFieldErrors)
          : error.message || "Unable to update the teacher profile."
      );

      if (hasFieldErrors) {
        focusFirstFieldError(nextFieldErrors, fieldRefs.current);
      }
    },
  });

  const handleLogout = () => {
    dispatch(clearTeacherSession());
    navigate("/teacherlogin", { replace: true });
  };

  const handleBack = () => {
    navigate("/teacherprofile");
  };

  const handleDashboardNavigation = () => {
    navigate("/teacherdashboard");
  };

  const handleImageFieldChange = (field, value) => {
    const nextError = getFieldValidationMessage(field, value);

    setFormState((previous) => ({
      ...previous,
      [field]: value,
    }));
    setLocalUploads((previous) => ({
      ...previous,
      [field]: "",
    }));
    setLocalUploadNames((previous) => ({
      ...previous,
      [field]: "",
    }));

    if (field === "photoImage" || field === "certificateImage") {
      setFieldErrors((previous) => ({
        ...previous,
        [field]: nextError,
      }));
      return;
    }

    setFieldErrors((previous) => ({
      ...previous,
      [field]: "",
    }));
  };

  const handleFieldChange = (field, value) => {
    const nextValue =
      field === "pricePerSession"
        ? value === "" || /^\d+$/.test(value)
          ? value
          : formState.pricePerSession
        : value;

    setFormState((previous) => ({
      ...previous,
      [field]: nextValue,
    }));
    setFieldErrors((previous) => ({
      ...previous,
      [field]: getFieldValidationMessage(field, nextValue),
    }));
  };

  const handlePhoneBlur = () => {
    const normalizedPhone = normalizeTeacherPhoneNumber(formState.phone);
    if (!normalizedPhone) {
      return;
    }

    setFormState((previous) => ({
      ...previous,
      phone: normalizedPhone,
    }));
    setFieldErrors((previous) => ({
      ...previous,
      phone: "",
    }));
  };

  const handleLocalImageUpload = async (field, file) => {
    if (!file) {
      return;
    }

    if (!String(file.type || "").startsWith("image/")) {
      const imageTypeError = "Please select an image file.";
      setFieldErrors((previous) => ({
        ...previous,
        [field]: imageTypeError,
      }));
      setStatusMessage(
        `${FIELD_STATUS_LABELS[field] || "Image"}: ${imageTypeError}`
      );
      return;
    }

    if (field === "photoImage" && !isAcceptedPhotoUpload(file)) {
      setFieldErrors((previous) => ({
        ...previous,
        photoImage: PHOTO_IMAGE_ERROR_MESSAGE,
      }));
      setStatusMessage(`Profile photo: ${PHOTO_IMAGE_ERROR_MESSAGE}`);
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setStatusMessage("");
      if (field === "photoImage") {
        setFieldErrors((previous) => ({
          ...previous,
          photoImage: "",
        }));
      } else {
        setFieldErrors((previous) => ({
          ...previous,
          [field]: "",
        }));
      }
      setLocalUploads((previous) => ({
        ...previous,
        [field]: dataUrl,
      }));
      setLocalUploadNames((previous) => ({
        ...previous,
        [field]: file.name,
      }));
      setFormState((previous) => ({
        ...previous,
        [field]: file.name,
      }));

      if (field === "photoImage") {
        setPhotoPreviewUrl(dataUrl);
        setPhotoPreviewFailed(false);
      }
    } catch (error) {
      setStatusMessage(
        `${FIELD_STATUS_LABELS[field] || "Image"}: ${
          error.message || "Unable to read the selected image."
        }`
      );
    }
  };

  const handleNumberFieldKeyDown = (
    event,
    field,
    minimumValue = 0,
    stepAmount = 1
  ) => {
    if (["-", "+", ".", "e", "E"].includes(event.key)) {
      event.preventDefault();
      return;
    }

    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
      return;
    }

    event.preventDefault();
    setFormState((previous) => {
      const parsedValue = Number(previous[field]);
      const currentValue = Number.isFinite(parsedValue) ? parsedValue : null;
      const isIncreasing = event.key === "ArrowUp";
      const nextValue =
        currentValue === null
          ? minimumValue > 0
            ? minimumValue
            : stepAmount
          : isIncreasing
            ? currentValue % stepAmount === 0
              ? currentValue + stepAmount
              : Math.floor(currentValue / stepAmount) * stepAmount + stepAmount
            : currentValue % stepAmount === 0
              ? currentValue - stepAmount
              : Math.ceil(currentValue / stepAmount) * stepAmount - stepAmount;
      const clampedValue = Math.max(minimumValue, nextValue);
      const serializedValue = String(
        Number.isInteger(clampedValue) ? clampedValue : clampedValue
      );

      setFieldErrors((previousErrors) => ({
        ...previousErrors,
        [field]: getFieldValidationMessage(field, serializedValue),
      }));

      return {
        ...previous,
        [field]: serializedValue,
      };
    });
  };

  const handleNumberFieldWheel = (event) => {
    event.currentTarget.blur();
    event.preventDefault();
  };

  const toggleChip = (key, value) => {
    setFormState((previous) => {
      const exists = previous[key].includes(value);
      const nextValues = exists
        ? previous[key].filter((item) => item !== value)
        : [...previous[key], value];

      setFieldErrors((previousErrors) => ({
        ...previousErrors,
        [key]: getFieldValidationMessage(key, nextValues),
      }));

      return {
        ...previous,
        [key]: nextValues,
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatusMessage("");
    const nextFieldErrors = getFormValidationErrors({
      formState,
      localUploads,
    });

    if (Object.values(nextFieldErrors).some(Boolean)) {
      setFieldErrors(nextFieldErrors);
      setStatusMessage(getFieldErrorStatusMessage(nextFieldErrors));
      focusFirstFieldError(nextFieldErrors, fieldRefs.current);
      return;
    }

    setFieldErrors(createInitialFieldErrors(teacherProfile));

    const payload = buildUpdatePayload(formState, initialFormRef.current);

    if (localUploads.photoImage) {
      payload.photoImage = localUploads.photoImage;
    }

    if (localUploads.certificateImage) {
      payload.certificateImage = localUploads.certificateImage;
    }

    if (!Object.keys(payload).length) {
      setStatusMessage("No profile fields have changed.");
      return;
    }

    updateProfileMutation.mutate(payload);
  };

  return (
    <>
      <style>{updateProfileStyles}</style>
      <main className="teacher-update-page">
        <header className="teacher-update-topbar">
          <div className="teacher-update-shell">
            <div className="teacher-update-topbar-row">
              <div className="teacher-update-brand">
                <button
                  className="teacher-update-back"
                  type="button"
                  aria-label="Back to teacher profile"
                  onClick={handleBack}
                >
                  <ArrowLeft size={18} />
                </button>
                <button
                  className="teacher-update-brand-badge"
                  type="button"
                  aria-label="Go to teacher dashboard"
                  onClick={handleDashboardNavigation}
                >
                  <BookOpen size={20} />
                </button>
                <h1 className="teacher-update-brand-title">Teacher Dashboard</h1>
              </div>

              <div className="teacher-update-actions">
                <div className="teacher-update-email-pill">
                  <Mail size={18} />
                  {teacherEmail}
                </div>
                <button
                  className="teacher-update-logout"
                  type="button"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="teacher-update-shell teacher-update-content">
          <section className="teacher-update-banner">
            <h1>Update teacher profile</h1>
            <p>Complete your profile to start teaching</p>
          </section>

          {profileQuery.isLoading ? (
            <div className="teacher-update-status teacher-update-status-success">
              Loading teacher profile...
            </div>
          ) : null}

          {profileQuery.isError ? (
            <div className="teacher-update-status teacher-update-status-error">
              {profileQuery.error.message}
            </div>
          ) : null}

          {statusMessage ? (
            <div className="teacher-update-status teacher-update-status-error">
              {statusMessage}
            </div>
          ) : null}

          <form onSubmit={handleSubmit}>
            <section className="teacher-update-card">
              <div className="teacher-update-card-title">
                <User size={18} />
                Update teacher profile
              </div>

              <div className="teacher-update-photo-row">
                <div className="teacher-update-photo-box" aria-hidden="true">
                  {photoPreviewUrl && !photoPreviewFailed ? (
                    <img
                      src={photoPreviewUrl}
                      alt={`${teacherName} profile`}
                      onError={() => setPhotoPreviewFailed(true)}
                    />
                  ) : (
                    getInitials(formState.name || teacherName)
                  )}
                </div>

              </div>

              <div className="teacher-update-grid">
                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-name">Full Name</label>
                  <input
                    id="teacher-update-name"
                    ref={(node) => {
                      fieldRefs.current.name = node;
                    }}
                    className={`teacher-update-input ${
                      fieldErrors.name ? "teacher-update-input-error" : ""
                    }`}
                    aria-invalid={fieldErrors.name ? "true" : "false"}
                    value={formState.name}
                    onChange={(event) => handleFieldChange("name", event.target.value)}
                    placeholder="John Doe"
                  />
                  {fieldErrors.name ? (
                    <p className="teacher-update-field-error">{fieldErrors.name}</p>
                  ) : null}
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-phone">Phone</label>
                  <input
                    id="teacher-update-phone"
                    ref={(node) => {
                      fieldRefs.current.phone = node;
                    }}
                    className={`teacher-update-input ${
                      fieldErrors.phone ? "teacher-update-input-error" : ""
                    }`}
                    aria-invalid={fieldErrors.phone ? "true" : "false"}
                    value={formState.phone}
                    onChange={(event) => handleFieldChange("phone", event.target.value)}
                    onBlur={handlePhoneBlur}
                    placeholder="9876543210"
                  />
                  {fieldErrors.phone ? (
                    <p className="teacher-update-field-error">{fieldErrors.phone}</p>
                  ) : null}
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-bio">Bio</label>
                  <textarea
                    id="teacher-update-bio"
                    ref={(node) => {
                      fieldRefs.current.bio = node;
                    }}
                    className={`teacher-update-textarea ${
                      fieldErrors.bio ? "teacher-update-input-error" : ""
                    }`}
                    aria-invalid={fieldErrors.bio ? "true" : "false"}
                    value={formState.bio}
                    onChange={(event) =>
                      handleFieldChange("bio", event.target.value)
                    }
                    placeholder="Write a short introduction about yourself"
                  />
                  {fieldErrors.bio ? (
                    <p className="teacher-update-field-error">{fieldErrors.bio}</p>
                  ) : null}
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-photo">Update Profile Pic</label>
                  <input
                    id="teacher-update-photo"
                    ref={(node) => {
                      fieldRefs.current.photoImage = node;
                    }}
                    className={`teacher-update-input ${
                      fieldErrors.photoImage ? "teacher-update-input-error" : ""
                    }`}
                    aria-invalid={fieldErrors.photoImage ? "true" : "false"}
                    value={localUploadNames.photoImage || formState.photoImage}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      handleImageFieldChange("photoImage", nextValue);
                      if (!getPhotoImageValidationMessage(nextValue)) {
                        setPhotoPreviewUrl(resolveApiImageUrl(nextValue));
                        setPhotoPreviewFailed(false);
                      }
                    }}
                    placeholder="https://example.com/photo.jpg"
                  />
                  <input
                    ref={photoFileInputRef}
                    className="teacher-update-file-input"
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      handleLocalImageUpload("photoImage", event.target.files?.[0]);
                      event.target.value = "";
                    }}
                  />
                  <div className="teacher-update-upload-actions">
                    <button
                      className="teacher-update-upload-button"
                      type="button"
                      onClick={() => photoFileInputRef.current?.click()}
                    >
                      <Upload size={16} />
                      Upload Photo
                    </button>
                    {localUploadNames.photoImage ? (
                      <span className="teacher-update-upload-file-name">
                        {localUploadNames.photoImage}
                      </span>
                    ) : null}
                  </div>
                  {fieldErrors.photoImage ? (
                    <p className="teacher-update-field-error">
                      {fieldErrors.photoImage}
                    </p>
                  ) : null}
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-certificate">
                    Certificate Image
                  </label>
                  <input
                    id="teacher-update-certificate"
                    ref={(node) => {
                      fieldRefs.current.certificateImage = node;
                    }}
                    className={`teacher-update-input ${
                      fieldErrors.certificateImage ? "teacher-update-input-error" : ""
                    }`}
                    aria-invalid={fieldErrors.certificateImage ? "true" : "false"}
                    value={
                      localUploadNames.certificateImage || formState.certificateImage
                    }
                    onChange={(event) =>
                      handleImageFieldChange("certificateImage", event.target.value)
                    }
                    placeholder="https://example.com/certificate.jpg"
                  />
                  <input
                    ref={certificateFileInputRef}
                    className="teacher-update-file-input"
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      handleLocalImageUpload(
                        "certificateImage",
                        event.target.files?.[0]
                      );
                      event.target.value = "";
                    }}
                  />
                  <div className="teacher-update-upload-actions">
                    <button
                      className="teacher-update-upload-button"
                      type="button"
                      onClick={() => certificateFileInputRef.current?.click()}
                    >
                      <Upload size={16} />
                      Upload Certificate
                    </button>
                    {localUploadNames.certificateImage ? (
                      <span className="teacher-update-upload-file-name">
                        {localUploadNames.certificateImage}
                      </span>
                    ) : null}
                  </div>
                  {fieldErrors.certificateImage ? (
                    <p className="teacher-update-field-error">
                      {fieldErrors.certificateImage}
                    </p>
                  ) : null}
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-qualification">Qualification</label>
                  <input
                    id="teacher-update-qualification"
                    ref={(node) => {
                      fieldRefs.current.qualifications = node;
                    }}
                    className={`teacher-update-input ${
                      fieldErrors.qualifications ? "teacher-update-input-error" : ""
                    }`}
                    aria-invalid={fieldErrors.qualifications ? "true" : "false"}
                    value={formState.qualifications}
                    onChange={(event) =>
                      handleFieldChange("qualifications", event.target.value)
                    }
                    placeholder="MSc Mathematics"
                  />
                  {fieldErrors.qualifications ? (
                    <p className="teacher-update-field-error">
                      {fieldErrors.qualifications}
                    </p>
                  ) : null}
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-experience">Years of Experience</label>
                  <input
                    id="teacher-update-experience"
                    ref={(node) => {
                      fieldRefs.current.experience = node;
                    }}
                    className={`teacher-update-input ${
                      fieldErrors.experience ? "teacher-update-input-error" : ""
                    }`}
                    aria-invalid={fieldErrors.experience ? "true" : "false"}
                    type="number"
                    min="0"
                    step="1"
                    value={formState.experience}
                    onChange={(event) =>
                      handleFieldChange("experience", event.target.value)
                    }
                    onKeyDown={(event) =>
                      handleNumberFieldKeyDown(event, "experience", 0)
                    }
                    onWheel={handleNumberFieldWheel}
                    placeholder="5"
                  />
                  {fieldErrors.experience ? (
                    <p className="teacher-update-field-error">
                      {fieldErrors.experience}
                    </p>
                  ) : null}
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-subjects">Subjects</label>
                  <textarea
                    id="teacher-update-subjects"
                    ref={(node) => {
                      fieldRefs.current.subjects = node;
                    }}
                    className={`teacher-update-textarea ${
                      fieldErrors.subjects ? "teacher-update-input-error" : ""
                    }`}
                    aria-invalid={fieldErrors.subjects ? "true" : "false"}
                    value={formState.subjects}
                    onChange={(event) =>
                      handleFieldChange("subjects", event.target.value)
                    }
                    placeholder="Maths"
                  />
                  {fieldErrors.subjects ? (
                    <p className="teacher-update-field-error">{fieldErrors.subjects}</p>
                  ) : null}
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-price">Price Per Session(in AUD)</label>
                  <input
                    id="teacher-update-price"
                    ref={(node) => {
                      fieldRefs.current.pricePerSession = node;
                    }}
                    className={`teacher-update-input ${
                      fieldErrors.pricePerSession ? "teacher-update-input-error" : ""
                    }`}
                    aria-invalid={fieldErrors.pricePerSession ? "true" : "false"}
                    type="number"
                    step={PRICE_PER_SESSION_INCREMENT}
                    min={PRICE_PER_SESSION_INCREMENT}
                    inputMode="numeric"
                    value={formState.pricePerSession}
                    onChange={(event) =>
                      handleFieldChange("pricePerSession", event.target.value)
                    }
                    onKeyDown={(event) =>
                      handleNumberFieldKeyDown(
                        event,
                        "pricePerSession",
                        PRICE_PER_SESSION_INCREMENT,
                        PRICE_PER_SESSION_INCREMENT
                      )
                    }
                    onWheel={handleNumberFieldWheel}
                    placeholder="20"
                  />
                  {fieldErrors.pricePerSession ? (
                    <p className="teacher-update-field-error">
                      {fieldErrors.pricePerSession}
                    </p>
                  ) : null}
                  <p className="teacher-update-field-note">
                    Each session duration is 1 hour.
                  </p>
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label>Years You Teach</label>
                  <div
                    ref={(node) => {
                      fieldRefs.current.yearsYouTeach = node;
                    }}
                    tabIndex={-1}
                    className={`teacher-update-chip-section ${
                      fieldErrors.yearsYouTeach
                        ? "teacher-update-chip-section-error"
                        : ""
                    }`}
                  >
                    <div className="teacher-update-chip-grid">
                      {TEACHER_YEAR_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          aria-pressed={formState.yearsYouTeach.includes(option.id)}
                          className={`teacher-update-chip ${
                            formState.yearsYouTeach.includes(option.id)
                              ? "teacher-update-chip-active"
                              : ""
                          }`}
                          onClick={() => toggleChip("yearsYouTeach", option.id)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {fieldErrors.yearsYouTeach ? (
                    <p className="teacher-update-field-error">
                      {fieldErrors.yearsYouTeach}
                    </p>
                  ) : null}
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label>Teaching Specializations</label>
                  <div
                    ref={(node) => {
                      fieldRefs.current.specializations = node;
                    }}
                    tabIndex={-1}
                    className={`teacher-update-chip-section ${
                      fieldErrors.specializations
                        ? "teacher-update-chip-section-error"
                        : ""
                    }`}
                  >
                    <div className="teacher-update-chip-grid teacher-update-chip-grid-specializations">
                      {SPECIALIZATION_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          aria-pressed={formState.specializations.includes(option)}
                          className={`teacher-update-chip ${
                            formState.specializations.includes(option)
                              ? "teacher-update-chip-active"
                              : ""
                          }`}
                          onClick={() => toggleChip("specializations", option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                  {fieldErrors.specializations ? (
                    <p className="teacher-update-field-error">
                      {fieldErrors.specializations}
                    </p>
                  ) : null}
                </div>
              </div>
            </section>

            <div className="teacher-update-footer">
              <button
                className="teacher-update-save"
                type="submit"
                disabled={updateProfileMutation.isPending}
              >
                {updateProfileMutation.isPending ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                   Update Profile
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </>
  );
}
